import { Injectable, inject, signal, computed } from '@angular/core';
import {
  Auth,
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithEmailLink,
  isSignInWithEmailLink,
  GoogleAuthProvider,
  updateProfile,
  signOut,
  authState,
} from '@angular/fire/auth';
import {
  Firestore,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Router } from '@angular/router';
import { from, of, switchMap, catchError } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { environment } from '../../../environments/environment';
import { AppUser, UserRole } from '../models/user.model';
import { ScheduleSettingsService } from './schedule-settings.service';
import { BookingDraftService, BookingDraft } from './booking-draft.service';

const EMAIL_FOR_SIGN_IN_KEY = 'seamus.emailForSignIn';
const PENDING_DISPLAY_NAME_KEY = 'seamus.pendingDisplayName';

function isOwnerEmail(email: string | null | undefined): boolean {
  return !!email && email.toLowerCase() === environment.ownerEmail.toLowerCase();
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly auth = inject(Auth);
  private readonly firestore = inject(Firestore);
  private readonly functions = inject(Functions);
  private readonly router = inject(Router);
  private readonly scheduleSettings = inject(ScheduleSettingsService);
  private readonly drafts = inject(BookingDraftService);

  readonly authUser$ = authState(this.auth);
  readonly firebaseUser = toSignal(this.authUser$, { initialValue: null });

  private readonly profileSignal = signal<AppUser | null>(null);
  readonly profile = this.profileSignal.asReadonly();
  readonly isLoggedIn = computed(() => !!this.firebaseUser());
  /** Owner if Firestore says so, or if the signed-in email matches ownerEmail */
  readonly isOwner = computed(
    () =>
      this.profileSignal()?.role === 'owner' ||
      isOwnerEmail(this.firebaseUser()?.email ?? this.profileSignal()?.email),
  );

  /** Surfaced on the login page after a failed auth attempt */
  readonly authError = signal<string | null>(null);

  constructor() {
    this.authUser$
      .pipe(
        switchMap((u) =>
          u
            ? from(this.ensureUserProfile(u)).pipe(
                catchError((err) => {
                  console.error('Failed to load user profile', err);
                  // Still expose a minimal profile so owner email can unlock the UI
                  return of({
                    uid: u.uid,
                    email: u.email,
                    displayName: u.displayName,
                    photoURL: u.photoURL,
                    role: (isOwnerEmail(u.email) ? 'owner' : 'customer') as UserRole,
                    createdAt: new Date(),
                  } satisfies AppUser);
                }),
              )
            : of(null),
        ),
      )
      .subscribe((profile) => this.profileSignal.set(profile));
  }

  async registerWithEmail(email: string, password: string, displayName: string): Promise<void> {
    const cred = await createUserWithEmailAndPassword(this.auth, email.trim(), password);
    await updateProfile(cred.user, { displayName: displayName.trim() });
    try {
      await this.ensureUserProfile(cred.user, displayName.trim());
    } catch (profileErr) {
      // Auth user already exists — surface a clearer follow-up than a bare Firebase error.
      console.error('Account created in Auth but profile write failed', profileErr);
      throw new Error(
        'Your login was created, but saving your profile failed. Try signing in now. If the owner dashboard is missing, confirm firestore.rules are deployed to this project.',
      );
    }
  }

  async loginWithEmail(email: string, password: string): Promise<void> {
    await signInWithEmailAndPassword(this.auth, email, password);
  }

  async loginWithGoogle(): Promise<void> {
    this.authError.set(null);
    await signInWithPopup(this.auth, new GoogleAuthProvider());
  }

  /**
   * Passwordless sign-in: Cloud Function generates the Auth link (Admin SDK) and
   * Resend delivers a branded email. When `draft` is provided (booking flow), the
   * draft is stored server-side so finishing the link still creates the booking.
   */
  async sendMagicLink(
    email: string,
    displayName?: string,
    draft?: BookingDraft | null,
  ): Promise<void> {
    this.authError.set(null);
    const trimmed = email.trim();
    if (!trimmed) {
      throw new Error('Enter your email address.');
    }

    const payloadDraft = draft === undefined ? this.drafts.peek() : draft;

    const send = httpsCallable<
      {
        email: string;
        displayName?: string;
        continueOrigin: string;
        draft?: Omit<BookingDraft, 'autoConfirm'>;
      },
      { ok: boolean; pendingId: string | null }
    >(this.functions, 'sendMagicLinkEmail');

    try {
      const result = await send({
        email: trimmed,
        displayName: displayName?.trim() || payloadDraft?.customerName || undefined,
        continueOrigin: window.location.origin,
        ...(payloadDraft
          ? {
              draft: {
                serviceId: payloadDraft.serviceId,
                date: payloadDraft.date,
                startTime: payloadDraft.startTime,
                endTime: payloadDraft.endTime,
                blockStartTime: payloadDraft.blockStartTime,
                blockEndTime: payloadDraft.blockEndTime,
                notes: payloadDraft.notes ?? '',
                locationPlace: payloadDraft.locationPlace,
                locationAddress: payloadDraft.locationAddress ?? '',
                quantity: payloadDraft.quantity || 1,
                customerName: displayName?.trim() || payloadDraft.customerName,
                customerEmail: trimmed,
              },
            }
          : {}),
      });
      const pendingId = result.data?.pendingId;
      if (pendingId) {
        this.drafts.savePendingId(pendingId);
      }
    } catch (err) {
      throw this.mapCallableError(err);
    }

    try {
      localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, trimmed);
      const name = displayName?.trim();
      if (name) {
        localStorage.setItem(PENDING_DISPLAY_NAME_KEY, name);
      } else {
        localStorage.removeItem(PENDING_DISPLAY_NAME_KEY);
      }
    } catch {
      /* private mode — complete page may ask for email again */
    }
  }

  /** Claim a server-stored draft after magic-link sign-in (one-time). */
  async claimPendingBooking(pendingId: string): Promise<BookingDraft> {
    const claim = httpsCallable<{ pendingId: string }, { draft: BookingDraft }>(
      this.functions,
      'claimPendingBooking',
    );
    try {
      const result = await claim({ pendingId });
      const draft = result.data?.draft;
      if (!draft?.serviceId || !draft?.date || !draft?.startTime) {
        throw new Error('Could not load your pending booking.');
      }
      return {
        ...draft,
        locationPlace: draft.locationPlace === 'provider' ? 'provider' : 'customer',
        locationAddress: draft.locationAddress ?? '',
        quantity: Math.max(1, Math.floor(Number(draft.quantity) || 1)),
        autoConfirm: true,
      };
    } catch (err) {
      throw this.mapCallableError(err);
    }
  }

  private mapCallableError(err: unknown): Error {
    const code = String((err as { code?: string })?.code ?? '');
    const message = String((err as { message?: string })?.message ?? '');

    if (code === 'functions/already-exists') {
      return new Error(
        message.replace(/^Firebase:\s*/i, '').replace(/\s*\([^)]*\)\.?\s*$/, '').trim() ||
          'This booking link was already used. Check My bookings.',
      );
    }
    if (code === 'functions/not-found' || code === 'functions/unimplemented') {
      return new Error(
        message.includes('pending') || message.includes('booking')
          ? message.replace(/^Firebase:\s*/i, '').replace(/\s*\([^)]*\)\.?\s*$/, '').trim() ||
            'This booking link was already used or expired. Please book again.'
          : 'Magic-link email isn’t deployed yet. Deploy Cloud Functions to this Firebase project, then try again.',
      );
    }
    if (code === 'functions/resource-exhausted') {
      return new Error('Too many email requests. Wait a minute and try again.');
    }
    if (code.startsWith('functions/')) {
      const cleaned = message.replace(/^Firebase:\s*/i, '').replace(/\s*\([^)]*\)\.?\s*$/, '').trim();
      return new Error(cleaned || 'Could not send the sign-in email. Try again.');
    }
    if (err instanceof Error) return err;
    return new Error(this.formatAuthError(err));
  }

  isMagicLinkUrl(url = typeof window !== 'undefined' ? window.location.href : ''): boolean {
    return !!url && isSignInWithEmailLink(this.auth, url);
  }

  peekEmailForMagicLink(): string | null {
    try {
      return localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Completes passwordless sign-in from the email link URL.
   * Pass `email` when localStorage is empty (e.g. opened on another device).
   * Pass `linkUrl` when completing from a stored href after an explicit Continue click
   * (avoids email-client prefetch consuming the one-time code on page load).
   */
  async completeMagicLinkSignIn(email?: string, linkUrl?: string): Promise<void> {
    this.authError.set(null);
    const href = linkUrl || window.location.href;
    if (!isSignInWithEmailLink(this.auth, href)) {
      throw new Error('This sign-in link is invalid or already used. Request a new one.');
    }

    let resolved =
      email?.trim() ||
      (() => {
        try {
          return localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
        } catch {
          return null;
        }
      })();

    if (!resolved) {
      throw new Error('Enter the same email you used when requesting the link.');
    }

    const cred = await signInWithEmailLink(this.auth, resolved, href);

    let pendingName: string | null = null;
    try {
      pendingName = localStorage.getItem(PENDING_DISPLAY_NAME_KEY);
      localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
      localStorage.removeItem(PENDING_DISPLAY_NAME_KEY);
    } catch {
      /* ignore */
    }

    if (pendingName?.trim()) {
      await this.applyDisplayName(pendingName.trim());
    } else {
      await this.ensureUserProfile(cred.user);
    }
  }

  /** Update Auth + Firestore profile display name (e.g. name typed on book confirm). */
  async applyDisplayName(displayName: string): Promise<void> {
    const user = this.auth.currentUser;
    const name = displayName.trim();
    if (!user || !name) return;

    await updateProfile(user, { displayName: name });
    const profile = await this.ensureUserProfile(user, name);
    if (profile.displayName !== name) {
      const ref = doc(this.firestore, `users/${user.uid}`);
      await setDoc(ref, { displayName: name }, { merge: true });
      this.profileSignal.set({ ...profile, displayName: name });
    } else {
      this.profileSignal.set(profile);
    }
  }

  async logout(): Promise<void> {
    await signOut(this.auth);
    this.profileSignal.set(null);
    await this.router.navigateByUrl('/');
  }

  formatAuthError(err: unknown): string {
    const code = String((err as { code?: string })?.code ?? '');
    const raw = err instanceof Error ? err.message : String(err ?? '');

    switch (code) {
      case 'auth/operation-not-allowed':
        return 'That sign-in method is not enabled yet. In Firebase Console → Authentication → Sign-in method, enable Email/Password and Email link (passwordless sign-in) for this project.';
      case 'auth/email-already-in-use':
        return 'An account already exists for this email. Try Sign in instead — or use Continue with Google if you signed up that way.';
      case 'auth/invalid-email':
        return 'That email address does not look valid.';
      case 'auth/invalid-action-code':
      case 'auth/expired-action-code':
        return 'That email link expired or was already used. Request a new link.';
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
      case 'auth/invalid-login-credentials':
        return 'Email or password is incorrect. If you booked with an email link, use “Email me a link” instead.';
      case 'auth/weak-password':
        return 'Password is too weak — use at least 6 characters.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Wait a minute and try again.';
      case 'auth/quota-exceeded':
        return 'Firebase hit its email-sending limit for this project (common while testing magic links). Wait about an hour, or sign in with Google for now. Raising the limit requires Blaze billing and/or custom SMTP under Authentication → Templates.';
      case 'auth/network-request-failed':
        return 'Network error — check your connection and try again.';
      case 'auth/unauthorized-domain':
        return 'This page’s domain is not authorized for sign-in. Add it under Firebase → Authentication → Settings → Authorized domains (e.g. shea-services-dev.web.app).';
      case 'auth/invalid-continue-uri':
      case 'auth/unauthorized-continue-uri':
      case 'auth/missing-continue-uri':
        return 'The sign-in link destination isn’t allowed. Add this site’s domain under Firebase → Authentication → Settings → Authorized domains.';
      case 'auth/popup-closed-by-user':
      case 'auth/cancelled-popup-request':
        return 'Sign-in was cancelled.';
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with this email using a different sign-in method. Try the other method (Google or email link).';
      case 'auth/requires-recent-login':
        return 'For security, sign out and sign in again, then retry.';
      default: {
        // Firebase messages look like "Firebase: Error (auth/email-already-in-use)."
        // Stripping that used to leave a useless "Error" — prefer the code, then raw.
        if (code.startsWith('auth/')) {
          return `Sign-in failed (${code.replace(/^auth\//, '')}). Try again or use a different method.`;
        }
        const cleaned = raw
          .replace(/^Firebase:\s*/i, '')
          .replace(/\s*\(auth\/[^)]+\)\.?\s*$/i, '')
          .trim();
        if (cleaned && cleaned.toLowerCase() !== 'error') {
          return cleaned;
        }
        return 'Something went wrong signing in. Check Firebase Authentication is set up for this project, then try again.';
      }
    }
  }

  private async ensureUserProfile(user: User, displayNameOverride?: string): Promise<AppUser> {
    const ref = doc(this.firestore, `users/${user.uid}`);
    const snap = await getDoc(ref);
    const role: UserRole = isOwnerEmail(user.email) ? 'owner' : 'customer';

    if (!snap.exists()) {
      await setDoc(ref, {
        uid: user.uid,
        email: user.email,
        displayName: displayNameOverride ?? user.displayName,
        photoURL: user.photoURL,
        role,
        createdAt: serverTimestamp(),
      });
      if (role === 'owner') {
        await this.publishOwnerUidSafe(user.uid);
      }
      return {
        uid: user.uid,
        email: user.email,
        displayName: displayNameOverride ?? user.displayName,
        photoURL: user.photoURL,
        role,
        createdAt: new Date(),
      };
    }

    const data = snap.data() as AppUser;
    if (displayNameOverride?.trim() && data.displayName !== displayNameOverride.trim()) {
      try {
        await setDoc(ref, { displayName: displayNameOverride.trim() }, { merge: true });
      } catch (err) {
        console.warn('Could not update displayName on user profile', err);
      }
      data.displayName = displayNameOverride.trim();
    }

    if (role === 'owner' && data.role !== 'owner') {
      try {
        await setDoc(
          ref,
          {
            role: 'owner',
            email: user.email ?? data.email,
          },
          { merge: true },
        );
      } catch (err) {
        console.warn(
          'Could not promote user to owner in Firestore. Deploy updated firestore.rules.',
          err,
        );
      }
      await this.publishOwnerUidSafe(user.uid);
      return {
        ...data,
        email: user.email ?? data.email,
        role: 'owner',
        createdAt: (data.createdAt as unknown as { toDate?: () => Date })?.toDate?.() ?? new Date(),
      };
    }

    if (role === 'owner' || data.role === 'owner') {
      await this.publishOwnerUidSafe(user.uid);
    }

    return {
      ...data,
      email: user.email ?? data.email,
      role: data.role === 'owner' || role === 'owner' ? 'owner' : 'customer',
      createdAt: (data.createdAt as unknown as { toDate?: () => Date })?.toDate?.() ?? new Date(),
    };
  }

  private async publishOwnerUidSafe(uid: string): Promise<void> {
    try {
      await this.scheduleSettings.publishOwnerUid(uid);
    } catch (err) {
      console.warn('Could not publish ownerUid to scheduleSettings', err);
    }
  }
}
